// External dependencies
import { useCallback, useEffect, useMemo, useRef, useState, type FC } from 'react';
import { useDispatch } from 'react-redux';

import { _isEmpty, _notNil } from '@/littledash';

// Model types
import type { CustomColumnSettings } from '@/components/UI/SpreadSheet/SpreadSheet.model';
import type { ID } from '@/model/Common.model';
import type {
  MetadataField,
  MetadataFieldWithValue,
  ModalMeta,
  StudyGroupsMetadataSettings,
} from '@/model/Metadata.model';
import type { TreatmentGroup } from '@/model/TreatmentGroup.model';

// Support modules
import { useFetchEntity } from '@/support/Hooks/fetch';
import { metadataMapToSortedMetadataArray, sanitizeMetadataValue, valueSet } from '@/support/hot';
import { RouteService } from '@/support/RouteService';
import { structuredCloneUtil } from '@/utils/browser.utils';
import { spreadSheetValidationTooltip } from '@/components/UI/SpreadSheet/SpreadSheet.utils';

// Internal modules
import Loading from '@/components/Loading';
import Button from '@/components/UI/Button';

// Relative imports
import TreatmentGroupsTable from './TreatmentGroupsTable';
import { generateColumnData, initialData } from './CreateTreatmentGroups.utils';
import './CreateTreatmentGroups.scss';

export interface TreatmentGroupKeyedMetadata extends Omit<TreatmentGroup, 'metadata'> {
  metadata: Record<string, MetadataFieldWithValue>;
}

import type Handsontable from 'handsontable';

type HotTableRefUnion = { hotInstance: Handsontable | null } | null;

const mapIncomingGroupData = (groups: Array<ModalMeta<TreatmentGroup>>): Array<TreatmentGroupKeyedMetadata> =>
  groups.map<TreatmentGroupKeyedMetadata>((group) => {
    const groupAnimalCount = group?.animals?.length ?? 0;
    const groupMeta = group?.metadata ?? group?.meta?.data ?? [];
    const metadata = groupMeta.reduce<Record<ID, MetadataFieldWithValue>>((acc, meta) => {
      if (_notNil(meta)) {
        acc[meta.glossary_id ?? meta.id] = { ...meta };
      }
      return acc;
    }, {});
    return { ...group, animals_count: groupAnimalCount, metadata };
  });

const getMetadataToRetain = (groups: Array<TreatmentGroupKeyedMetadata>): Set<ID> =>
  groups.reduce<Set<ID>>((acc, group) => {
    Object.values(group.metadata ?? {}).forEach(({ glossary_id: glossaryId, value, field_type: fieldType }) => {
      const sanitizedValue = sanitizeMetadataValue(value, fieldType);
      const hasValue = valueSet(sanitizedValue);
      if (hasValue) {
        acc.add(glossaryId);
      }
    });
    return acc;
  }, new Set());

interface CreateTreatmentGroupsProps {
  groups: Array<ModalMeta<TreatmentGroup>>;
  onAddGroups: (groups: Array<TreatmentGroup>) => void;
}

const CreateTreatmentGroups: FC<CreateTreatmentGroupsProps> = ({ groups, onAddGroups }) => {
  const [metadata, setMetadata] = useState<Array<MetadataField>>([]);

  const [columnData, updateColumnData] = useState<{
    colHeaders: Array<string>;
    columns: Array<CustomColumnSettings>;
  }>({
    colHeaders: [],
    columns: [],
  });
  const [isInvalid, setIsInvalid] = useState<boolean>(false);

  const initialGroupData = useMemo<Array<TreatmentGroupKeyedMetadata>>(
    () => structuredCloneUtil<Array<TreatmentGroupKeyedMetadata>>(initialData),
    []
  );

  const [groupData, setGroupData] = useState<{ groups: Array<TreatmentGroupKeyedMetadata> }>({
    groups: _isEmpty(groups) ? initialGroupData : mapIncomingGroupData(groups),
  });

  const ref = useRef<HotTableRefUnion>(null);
  const dispatch = useDispatch();

  const validateRows = useCallback(
    async () =>
      new Promise<boolean>((resolve) => {
        const hot = ref.current?.hotInstance;
        if (_notNil(hot)) {
          try {
            hot.validateRows(
              Array.from({ length: hot.countRows() - 1 }).map((_, idx) => idx),
              (valid: boolean) => {
                resolve(valid);
                hot.render();
              }
            );
          } catch {
            resolve(false);
          }
        } else {
          resolve(false);
        }
      }),
    [ref]
  );

  useEffect(() => {
    setGroupData({
      groups: _isEmpty(groups) ? initialGroupData : mapIncomingGroupData(groups),
    });
  }, [groups, initialGroupData]);

  const { entity: groupMetadata, entityLoading: groupMetadataLoading } = useFetchEntity<Array<MetadataField>>({
    entityType: 'glossaryMeta',
    queryParams: {
      filter_type: 'group_meta',
    },
  });

  const { entity: studyGroupsMetadataSettings, entityLoading: studyGroupsMetadataSettingsLoading } = useFetchEntity<
    Array<StudyGroupsMetadataSettings>
  >({
    entityType: 'studyGroups',
  });

  useEffect(() => {
    if (
      columnData.colHeaders.length === 0 &&
      _isEmpty(metadata) &&
      _notNil(groupMetadata) &&
      _notNil(studyGroupsMetadataSettings) &&
      _notNil(groups)
    ) {
      const metadataMap = new Map(groupMetadata?.map((meta) => [meta.id, meta]) ?? []);

      groups.forEach((group) =>
        group?.metadata?.forEach((md) => {
          const { glossary_id: id, options, title, active } = md;
          if (!metadataMap.has(id)) {
            metadataMap.set(id, {
              id,
              glossary_id: id,
              field_type: md.field_type,
              options,
              read_only: md.read_only,
              title,
              created_at: md.created_at,
              updated_at: md.updated_at,
              active,
              slug: md.slug,
              required: md.required ?? false,
            });
          }
        })
      );

      const requiredMetadata = new Map(studyGroupsMetadataSettings?.map(({ id, required }) => [id, required]) ?? []);
      groups.forEach((g) => {
        (g?.metadata ?? []).forEach((m) => {
          const id = m?.glossary_id ?? m?.id;
          if (_notNil(id) && !requiredMetadata.has(id)) {
            requiredMetadata.set(id, false);
          }
        });
      });

      updateColumnData(
        generateColumnData(
          [...requiredMetadata.entries()].map(([metadataId, required]) => ({
            ...metadataMap.get(metadataId),
            required,
          }))
        )
      );
      setMetadata(groupMetadata.filter(({ id }) => !requiredMetadata.has(id)));
    }
  }, [groupMetadata, studyGroupsMetadataSettings, groups, metadata, columnData.colHeaders.length]);

  const closeModal = () => {
    dispatch({
      type: 'CLOSE_MODAL',
    });
  };

  const submitGroups = async () => {
    const valid = await validateRows();
    if (valid && _notNil(onAddGroups)) {
      const metadataGlossaryIdOrder = columnData.columns.reduce<Array<number>>((acc, column) => {
        const metaId = Number(column.metaID);
        if (_notNil(column.metaID) && !Number.isNaN(metaId)) {
          acc.push(metaId);
        }
        return acc;
      }, []);
      const groupsToSubmit = groupData.groups.slice(0, groupData.groups.length - 1);
      const metadataIdsToRetain = getMetadataToRetain(groupsToSubmit);

      onAddGroups(
        groupsToSubmit.map((group) => ({
          ...group,
          metadata: metadataMapToSortedMetadataArray(metadataGlossaryIdOrder, metadataIdsToRetain, group.metadata),
        }))
      );

      closeModal();
    }
  };

  const TableComponent = (
    <TreatmentGroupsTable
      groupData={groupData}
      setGroupData={setGroupData}
      columnData={{
        colHeaders: columnData.colHeaders,
        columns: columnData.columns,
      }}
      updateColumnData={updateColumnData}
      metadata={metadata}
      setMetadata={setMetadata}
      setIsInvalid={setIsInvalid}
      innerRef={ref}
    />
  );

  return (
    <div
      style={{ minHeight: '90vh' }}
      data-test-component="CreateTreatmentGroups"
      data-test-element="container"
      className="flex flex-wrap w-90 overflow-hidden center ma3 bg-light-gray br2 mt4 mb-2 relative"
    >
      <div className="flex flex-wrap self-start w-100" style={{ minHeight: 'calc( 90vh - 72px)' }}>
        <div className="ph4 pt4 pb3 relative z-5">
          <h3 className="normal f4 lh-title">Setup treatment groups</h3>
          <p className="lh-copy f5 pv2">
            Click a field to update group information or add a new metadata column.
            <a
              href={RouteService.help('treatment_groups')}
              target="_blank"
              rel="noopener noreferrer"
              className="link blue"
            >
              {' '}
              Read more
            </a>
          </p>
        </div>
        {groupMetadataLoading || studyGroupsMetadataSettingsLoading ? (
          <div className="w-100 h-100">
            <Loading />
          </div>
        ) : (
          <div
            className="ph4 pb4 w-100 ow-spreadsheet-styles"
            style={{ marginTop: '-50px' }}
            data-test-component="CreateTreatmentGroups"
            data-test-element="spreadsheet-container"
          >
            {TableComponent}
          </div>
        )}
      </div>
      <div
        className="ph4 pv3 flex self-end bt b--moon-gray w-100"
        data-test-component="CreateTreatmentGroups"
        data-test-element="action-container"
      >
        <Button
          onClick={submitGroups}
          testId="save-action"
          disabled={groupMetadataLoading || studyGroupsMetadataSettingsLoading || isInvalid}
          tooltip={isInvalid ? spreadSheetValidationTooltip : undefined}
        >
          Save
        </Button>
        <Button plain className="ml3" onClick={closeModal} testId="cancel-action">
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default CreateTreatmentGroups;
